export type StockStatus = 'out' | 'low' | 'in_transit' | 'in_stock' | 'overstock';

export interface StockStatusInput {
  /** Total on-hand across every location, in the UoM Produced. */
  onHand: number;
  /** Placed POs and confirmed production orders not yet received. Drafts don't count. */
  onOrder: number;
  reorderPoint: number | null;
  reorderQty: number | null;
  /** Per-item override for the overstock limit (`items.overstock_at`). */
  overstockAt?: number | null;
}

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  out: 'Out',
  low: 'Low',
  in_transit: 'In transit',
  in_stock: 'In stock',
  overstock: 'Overstock',
};

/** Most urgent first; also the sort order of the Stock status column. */
export const STOCK_STATUSES: StockStatus[] = ['out', 'low', 'in_transit', 'in_stock', 'overstock'];

/** Filter value for an active item that has no reorder point, so no status. */
export const NO_REORDER_POINT = 'no_reorder_point';

/**
 * The Stock status column's cell value. Empty for rows that can't have a
 * status at all (archived items), so they match no filter option.
 */
export function stockStatusFilterValue(input: StockStatusInput | null | undefined): string {
  if (!input) return '';
  return getStockStatus(input) ?? NO_REORDER_POINT;
}

export function compareStockStatusValues(a: string, b: string): number {
  const rank = (v: string) => {
    const i = STOCK_STATUSES.indexOf(v as StockStatus);
    if (i !== -1) return i;
    return v === NO_REORDER_POINT ? STOCK_STATUSES.length : STOCK_STATUSES.length + 1;
  };
  return rank(a) - rank(b);
}

/**
 * The most stock normal replenishment should leave on the shelf: a
 * reorder fires at the reorder point and brings in the reorder quantity.
 * Null when either is missing.
 */
export function defaultOverstockAt(
  reorderPoint: number | null,
  reorderQty: number | null,
): number | null {
  if (reorderPoint == null || !reorderQty || reorderQty <= 0) return null;
  return reorderPoint + reorderQty;
}

/** On-hand above this is overstock: the item's override, else the default. */
export function overstockThreshold({
  reorderPoint,
  reorderQty,
  overstockAt,
}: Pick<StockStatusInput, 'reorderPoint' | 'reorderQty' | 'overstockAt'>): number | null {
  return overstockAt ?? defaultOverstockAt(reorderPoint, reorderQty);
}

/**
 * Stock health against the item's reorder point. Items without a reorder
 * point aren't managed to a level, so they get no status.
 *
 * Out wins over In transit: incoming supply doesn't help an order that
 * has to ship today.
 */
export function getStockStatus(input: StockStatusInput): StockStatus | null {
  const { onHand, onOrder, reorderPoint } = input;
  if (reorderPoint == null) return null;
  if (onHand <= 0) return 'out';
  if (onHand <= reorderPoint) return onHand + onOrder > reorderPoint ? 'in_transit' : 'low';
  const max = overstockThreshold(input);
  if (max != null && onHand > max) return 'overstock';
  return 'in_stock';
}

export function describeStockStatus(input: StockStatusInput, uom: string): string {
  const { onHand, onOrder, reorderPoint } = input;
  const parts = [`${onHand.toLocaleString()} ${uom} on hand`];
  if (reorderPoint != null) parts.push(`reorder at ${reorderPoint.toLocaleString()}`);
  if (onOrder > 0) parts.push(`${onOrder.toLocaleString()} on order`);
  const max = overstockThreshold(input);
  if (max != null) parts.push(`overstock above ${max.toLocaleString()}`);
  return parts.join(' · ');
}
